import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  Close,
  CheckCircle,
  Error,
  Warning,
} from '@mui/icons-material';
import { AuthorItem } from '../../types/item';

interface PreviewPanelProps {
  item: AuthorItem;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({ item }) => {
  const [showPreview, setShowPreview] = useState(false);

  const validateItem = () => {
    const issues = [];
    
    if (!item.question?.trim()) {
      issues.push({ type: 'error', message: 'Question text is required' });
    }
    
    const filledOptions = item.options.filter(opt => opt.trim());
    if (filledOptions.length < 2) {
      issues.push({ type: 'error', message: 'At least 2 options are required' });
    }
    
    const answerIndex = item.answerIndex || 0;
    if (answerIndex < 0 || answerIndex >= item.options.length) {
      issues.push({ type: 'error', message: 'Valid correct answer must be selected' });
    }

    if (!item.options[answerIndex]?.trim()) {
      issues.push({ type: 'error', message: 'Correct answer option cannot be empty' });
    }
    
    // Remove taxonomy validation since it's not part of the learning-service model
    
    if (!item.explanation?.trim()) {
      issues.push({ type: 'warning', message: 'Explanation is recommended' });
    }
    
    if (item.tags.length === 0) {
      issues.push({ type: 'info', message: 'Tags help with searchability' });
    }

    return issues;
  };

  const validationIssues = validateItem();
  const hasErrors = validationIssues.some(issue => issue.type === 'error');
  const hasWarnings = validationIssues.some(issue => issue.type === 'warning');

  const getValidationIcon = () => {
    if (hasErrors) return <Error color="error" />;
    if (hasWarnings) return <Warning color="warning" />;
    return <CheckCircle color="success" />;
  };

  const getValidationColor = () => {
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'success';
  };

  const getValidationMessage = () => {
    if (hasErrors) return 'Has errors';
    if (hasWarnings) return 'Has warnings';
    return 'Ready to save';
  };

  const getOptionLabel = (index: number) => {
    return String.fromCharCode(65 + index); // A, B, C, D, E, F
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        👁️ Preview
      </Typography>

      {/* Validation Status */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: `${getValidationColor()}.50` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {getValidationIcon()}
          <Typography variant="subtitle2" color={`${getValidationColor()}.main`}>
            {getValidationMessage()}
          </Typography>
        </Box>
        
        {validationIssues.length > 0 && (
          <Box>
            {validationIssues.slice(0, 3).map((issue, index) => (
              <Typography 
                key={index}
                variant="caption" 
                display="block" 
                color={`${issue.type === 'info' ? 'text.secondary' : issue.type + '.main'}`}
              >
                • {issue.message}
              </Typography>
            ))}
            {validationIssues.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{validationIssues.length - 3} more issues
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Quick Stats */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          📊 Content Stats
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          📝 Question: {stripHtml(item.question).length} chars
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          🎯 Options: {item.options.filter(Boolean).length}/{item.options.length}
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          ✅ Correct: Option {(item.answerIndex || 0) >= 0 ? getOptionLabel(item.answerIndex || 0) : 'None'}
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          💡 Explanation: {stripHtml(item.explanation || '').length} chars
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary">
          🎵 Media: {item.mediaUrl ? 'Yes' : 'No'}
        </Typography>
      </Box>

      {/* Preview Button */}
      <Button
        variant="outlined"
        fullWidth
        onClick={() => setShowPreview(true)}
        startIcon={<Visibility />}
        disabled={hasErrors}
      >
        Show Student View
      </Button>

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Student Preview</Typography>
          <IconButton onClick={() => setShowPreview(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          {/* Question */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Question:
            </Typography>
            <Box 
              dangerouslySetInnerHTML={{ __html: item.question }}
              sx={{ mb: 2 }}
            />
            
            {/* Media URL */}
            {item.mediaUrl && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Media:
                </Typography>
                <audio controls style={{ width: '100%' }}>
                  <source src={item.mediaUrl} />
                  Your browser does not support the audio element.
                </audio>
              </Box>
            )}
            
            {/* Options */}
            <Typography variant="subtitle2" gutterBottom>
              Options:
            </Typography>
            <Box sx={{ pl: 2 }}>
              {item.options.map((option, index) => (
                option.trim() && (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: index === (item.answerIndex || 0) ? 'success.50' : 'grey.50',
                      border: 1,
                      borderColor: index === (item.answerIndex || 0) ? 'success.main' : 'grey.200',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mr: 2 }}>
                      {getOptionLabel(index)})
                    </Typography>
                    <Box dangerouslySetInnerHTML={{ __html: option }} />
                    {index === (item.answerIndex || 0) && (
                      <CheckCircle color="success" sx={{ ml: 'auto' }} />
                    )}
                  </Box>
                )
              ))}
            </Box>
          </Paper>

          {/* Explanation */}
          {item.explanation && (
            <Paper sx={{ p: 3, mb: 2, bgcolor: 'info.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                💡 Explanation:
              </Typography>
              <Box dangerouslySetInnerHTML={{ __html: item.explanation }} />
            </Paper>
          )}

          {/* Metadata */}
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" gutterBottom>
              📋 Metadata:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`Score: ${item.score || 1}`} size="small" />
              <Chip label={`Duration: ${item.duration || 60}s`} size="small" />
              {item.cognitiveSkill && (
                <Chip label={item.cognitiveSkill} size="small" />
              )}
            </Box>
            {item.tags.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Tags: {item.tags.join(', ')}
                </Typography>
              </Box>
            )}
          </Paper>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PreviewPanel;
